import {
  CheckBox,
  CheckBoxOutlineBlank,
  Close,
  ContactSupport,
  ExpandMore,
  Fullscreen
} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material'
import { TransitionUp } from '../Helpers/Drag'
import { useEffect, useState } from 'react'
import PulseComponent from './Pulse'
import { getIcon } from './Helpers/Icon'
import RTEView from '../RichTextEditor/RTEView'
import ABTesting from './ABTesting'
import InfoDialog from './InfoDialog'
import MultipleChoiceGrid from './MultipleChoiceGrid'

const ViewPulseBody = ({
  pulse,
  uid,
  comments,
  setComments,
  isAllowedToComment,
  displayName,
  showInfo,
  setShowInfo,
  isPreview
}) => {
  const [pulseSections, _setPulseSections] = useState(pulse.pulseSections || [])
  const pulseValues = pulse.pulseValues || []
  const [showHelp, setShowHelp] = useState(true)
  const [tabValue, setTabValue] = useState(0)
  const [headers, setHeaders] = useState([])
  const [expanded, setExpanded] = useState(false)
  const [handleFullScreen, setHandleFullScreen] = useState(false)
  const [fullScreenImage, setFullScreenImage] = useState(null)
  const [target, setTarget] = useState(null)

  const setHandleFullScreenSwitch = () => {
    setHandleFullScreen(false)
    setFullScreenImage(null)
  }

  const setFullScreenImageSwitch = image => {
    setFullScreenImage(image)
    setHandleFullScreen(true)
  }

  const handleTabChange = (_event, newValue) => {
    let visibleSections = pulseSections.filter(section => {
      if (section.header) {
        if (tabValue === 0) return true
        return (
          section.header.title === headers[tabValue].title &&
          section.header.icon === headers[tabValue].icon
        )
      }
      return true
    })
    if (visibleSections.length === 0) {
      setExpanded(false)
    } else {
      setExpanded(`0_section`)
    }
    setTabValue(newValue)
  }

  const handleChange = panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
    if (isExpanded) {
      setTarget(event.currentTarget)
    }
  }

  useEffect(() => {
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 500)
      setTarget(null)
    }
  }, [target])

  useEffect(() => {
    setHeaders(
      Object.values(
        pulseSections.reduce((acc, section) => {
          if (section.header) {
            if (!acc.length) {
              acc = [
                {
                  title: 'All',
                  icon: 'all_inclusive'
                }
              ]
            }
            if (!acc.some(val => val.title === section.header.title)) {
              acc = [...acc, { ...section.header }]
            }
          }
          return acc
        }, [])
      )
    )
  }, [pulseSections])

  const hasUserVotedOnAll = () => {
    let hasVoted = true
    pulseSections.forEach((section, sectionIndex) => {
      hasVoted = hasUserVoted(section, sectionIndex) && hasVoted
    })
    return hasVoted
  }

  const hasUserVoted = (section, sectionIndex) => {
    if (section.isABTesting || section.isMultipleChoice || section.isChoice) {
      let commentsArray = comments.filter(
        comment => comment.sectionIndex === sectionIndex && comment.uid === uid
      )
      return commentsArray.length > 0
    } else {
      let hasVoted = true
      section.pulsePrompts.forEach((_prompt, promptIndex) => {
        let commentsArray = comments.filter(
          comment =>
            comment.sectionIndex === sectionIndex &&
            comment.pulseIndex === promptIndex &&
            comment.uid === uid
        )
        if (commentsArray.length === 0) {
          hasVoted = false
        }
      })
      return hasVoted
    }
  }

  const hasNextSection = currentSection => {
    let headerSections = getHeaderSections()
    let currentSectionIndex = headerSections.findIndex(
      sec => sec === currentSection
    )
    if (currentSectionIndex < headerSections.length - 1) {
      return true
    } else if (tabValue !== 0 && currentSection.header) {
      let nextHeaderIndex =
        headers.findIndex(
          header =>
            currentSection.header &&
            header.title === currentSection.header.title &&
            header.icon === currentSection.header.icon
        ) + 1
      if (nextHeaderIndex < headers.length) {
        let nextHeaderSections = pulseSections.filter(section => {
          if (section.header) {
            return (
              section.header.title === headers[nextHeaderIndex].title &&
              section.header.icon === headers[nextHeaderIndex].icon
            )
          }
          return false
        })
        return nextHeaderSections.length > 0
      }
    }
    return false
  }

  const goToNextSection = section => {
    let headerSections = getHeaderSections()
    let nextSectionIndex = headerSections.findIndex(sec => sec === section) + 1
    if (nextSectionIndex < headerSections.length) {
      setExpanded(`${nextSectionIndex}_section`)
      setTarget(document.getElementById(`${nextSectionIndex}_section`))
    } else if (tabValue !== 0 && section.header) {
      let nextHeaderIndex =
        headers.findIndex(
          header =>
            header.title === section.header.title &&
            header.icon === section.header.icon
        ) + 1
      if (nextHeaderIndex < headers.length) {
        setTabValue(nextHeaderIndex)
        setExpanded(`0_section`)
        setTarget(document.getElementById(`pulse-header-tabs`))
      }
    }
  }

  const getHeaderSections = () => {
    return pulseSections.filter(section => {
      if (section.header) {
        if (tabValue === 0) return true
        return (
          section.header.title === headers[tabValue].title &&
          section.header.icon === headers[tabValue].icon
        )
      }
      return true
    })
  }

  const formatMessageWithLinks = text => {
    const urlRegex =
      /((?:https?:\/\/|www\.)[^\s]+|[a-zA-Z0-9.-]+\.(?:com|net|org|io|edu)(?:\/[^\s]*)?)/g

    return text.split(urlRegex).map((part, index) => {
      if (part.match(urlRegex)) {
        const href = part.startsWith('http') ? part : `https://${part}`

        return (
          <a
            key={index}
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: 'unset' }}
          >
            {part}
          </a>
        )
      }
      return part
    })
  }
  const getSectionIndex = section => {
    return pulseSections.findIndex(sec => sec === section)
  }

  const SectionImageComponent = ({ section }) => {
    let sectionIndex = pulseSections.findIndex(sec => sec === section)
    if (pulse.sectionImages && pulse.sectionImages[sectionIndex]) {
      return (
        <Stack alignItems='center'>
          <Box position='relative' display='inline-block'>
            <img
              src={pulse.sectionImages[sectionIndex]}
              alt={
                pulse.sectionImageAlts && pulse.sectionImageAlts[sectionIndex]
                  ? pulse.sectionImageAlts[sectionIndex]
                  : `Image for ${section.title}`
              }
              style={{
                maxHeight: 'min(50vh, 500px)',
                marginBottom: '16px',
                cursor: 'pointer',
                maxWidth: '100%'
              }}
              loading='lazy'
              onClick={() =>
                setFullScreenImageSwitch(pulse.sectionImages[sectionIndex])
              }
            />
            <IconButton
              aria-label='view full screen'
              color='primary'
              onClick={() =>
                setFullScreenImageSwitch(pulse.sectionImages[sectionIndex])
              }
              sx={{
                position: 'absolute',
                top: 8,
                right: 8
              }}
            >
              <Fullscreen />
            </IconButton>
          </Box>
        </Stack>
      )
    } else {
      return <></>
    }
  }

  return (
    <Box
      sx={{
        m: { md: 2, xs: '8px 8px 16px' },
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2
      }}
    >
      {showHelp && (
        <Alert
          onClick={() => setShowInfo(true)}
          severity='info'
          sx={{
            width: '100%'
          }}
          icon={<ContactSupport />}
          action={
            <>
              <Button
                sx={{ display: { md: 'block', xs: 'none' }, marginRight: 1 }}
                variant='contained'
                size='small'
                color='secondary'
              >
                How to use a Pulse
              </Button>
              <IconButton
                size='small'
                color='inherit'
                onClick={e => {
                  e.stopPropagation()
                  setShowHelp(false)
                }}
              >
                <Close />
              </IconButton>
            </>
          }
        >
          Click here to learn how to use and respond to a Pulse.
        </Alert>
      )}
      {headers.length > 1 && (
        <Box sx={{ width: '100%' }}>
          <Card
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              border: 'none'
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label='Header Tabs'
              variant='scrollable'
              allowScrollButtonsMobile
              id='pulse-header-tabs'
            >
              {headers.map((section, index) => (
                <Tab
                  icon={getIcon(section.icon, false)}
                  iconPosition='start'
                  label={
                    section.title.substring(0, 20) +
                    (section.title.length > 20 ? '...' : '')
                  }
                  id={`simple-tab-${index}`}
                  key={`simple-tab-${index}`}
                  aria-controls={`simple-tabpanel-${index}`}
                />
              ))}
            </Tabs>
          </Card>
          {headers.map((section, index) => (
            <div
              role='tabpanel'
              hidden={tabValue !== index}
              id={`simple-tabpanel-${index}`}
              key={`simple-tabpanel-${index}`}
              aria-labelledby={`simple-tab-${index}`}
            >
              {section.title && tabValue === index && tabValue !== 0 && (
                <Box sx={{ p: 3 }}>
                  {section.icon && getIcon(section.icon)}
                  {section.title}
                </Box>
              )}
            </div>
          ))}
        </Box>
      )}
      {pulseSections
        .filter(section => {
          if (section.header) {
            if (tabValue === 0) return true
            return (
              section.header.title === headers[tabValue].title &&
              section.header.icon === headers[tabValue].icon
            )
          }
          return true
        })
        .map((section, sectionIndex) => (
          <Card raised sx={{ width: '100%' }} key={sectionIndex}>
            <Accordion
              expanded={expanded === `${sectionIndex}_section`}
              onChange={handleChange(`${sectionIndex}_section`)}
              className='pulse-section'
              id={`${sectionIndex}_section`}
              sx={{ marginBottom: 0 }}
            >
              <AccordionSummary
                sx={{
                  textAlign: 'left',
                  fontWeight: 'bold'
                }}
                expandIcon={<ExpandMore />}
              >
                <Box
                  sx={{
                    overflowWrap: 'anywhere',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    mr: 1
                  }}
                >
                  {section.header && tabValue === 0 && (
                    <Tooltip title={section.header.title}>
                      <IconButton>
                        {getIcon(section.header.icon, false)}
                      </IconButton>
                    </Tooltip>
                  )}
                  {section.title}
                </Box>
                <Box
                  sx={{
                    ml: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    mr: 1
                  }}
                >
                  {hasUserVoted(section, sectionIndex) ? (
                    <Tooltip title='You have responded to this section'>
                      <CheckBox color='success' />
                    </Tooltip>
                  ) : (
                    <Tooltip title='You have not responded to this section'>
                      <CheckBoxOutlineBlank />
                    </Tooltip>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ textAlign: 'left' }}>
                {section.subtitle && section.subtitle !== '<p></p>' && (
                  <RTEView content={section.subtitle} mb={2} />
                )}
                <SectionImageComponent section={section} />
                {!section.isABTesting &&
                !section.isMultipleChoice &&
                !section.isChoice ? (
                  section.pulsePrompts.map((val, pulseIndex) => (
                    <PulseComponent
                      key={sectionIndex + 'pulse' + pulseIndex}
                      pulsePrompt={val}
                      pulseValues={pulseValues}
                      comments={comments}
                      setComments={setComments}
                      isAllowedToComment={isAllowedToComment}
                      displayName={displayName}
                      uid={uid}
                      document={pulse}
                      sectionIndex={getSectionIndex(section)}
                      pulseIndex={pulseIndex}
                      hasNextSection={hasNextSection}
                      goToNextSection={goToNextSection}
                      pulseTotalPrompts={section.pulsePrompts.length}
                      section={section}
                      isPreview={isPreview}
                    />
                  ))
                ) : !section.isMultipleChoice && !section.isChoice ? (
                  <ABTesting
                    section={section}
                    comments={comments}
                    setComments={setComments}
                    isAllowedToComment={isAllowedToComment}
                    displayName={displayName}
                    uid={uid}
                    document={pulse}
                    sectionIndex={getSectionIndex(section)}
                    setFullScreenImageSwitch={setFullScreenImageSwitch}
                    hasNextSection={hasNextSection}
                    goToNextSection={goToNextSection}
                    isPreview={isPreview}
                  />
                ) : (
                  <MultipleChoiceGrid
                    section={section}
                    comments={comments}
                    setComments={setComments}
                    isAllowedToComment={isAllowedToComment}
                    displayName={displayName}
                    uid={uid}
                    document={pulse}
                    sectionIndex={getSectionIndex(section)}
                    hasNextSection={hasNextSection}
                    goToNextSection={goToNextSection}
                    isPreview={isPreview}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          </Card>
        ))}
      {hasUserVotedOnAll() && (
        <Alert
          severity='success'
          variant='filled'
          icon={<CheckBox sx={{ alignSelf: 'center' }} fontSize='inherit' />}
          sx={{ width: '100%' }}
        >
          <Typography variant='h6'>
            {pulse.thankYouMessage
              ? formatMessageWithLinks(pulse.thankYouMessage)
              : 'Thank you for responding to this Pulse! Feel free to change your responses or leave comments within each section.'}
          </Typography>
        </Alert>
      )}
      <Dialog
        open={handleFullScreen}
        onClose={setHandleFullScreenSwitch}
        fullScreen
        TransitionComponent={TransitionUp}
        className='fullscreen'
      >
        <Stack
          sx={{
            width: '100%',
            height: '100%',
            backgroundColor: 'gray',
            position: 'relative'
          }}
          justifyContent='center'
          alignItems='center'
        >
          <IconButton
            onClick={setHandleFullScreenSwitch}
            size='large'
            aria-label='close full screen'
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              background: 'black'
            }}
          >
            <Close />
          </IconButton>
          {fullScreenImage && (
            <img
              src={fullScreenImage}
              alt='Full Screen'
              style={{ maxWidth: '100%', maxHeight: '100%' }}
            />
          )}
        </Stack>
      </Dialog>
      <InfoDialog showInfo={showInfo} setShowInfo={setShowInfo} />
    </Box>
  )
}

export default ViewPulseBody
